import {Accordion} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Container} from 'src/design/atoms/Container';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {List} from 'src/design/atoms/List';
import {Loader} from 'src/design/atoms/Loader';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {IconMail} from '@tabler/icons-react';
import {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {UserValidationStatusEnum} from 'src/api/ProactApiClient';
import {ServerErrorAlert} from 'src/app/layout/ServerErrorAlert';
import {useSendMagicLinkMutation} from 'src/redux/queries/user';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {setUserAccountStatus} from 'src/redux/slices/authSlice';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

// A page to send a magic link to the user's email. The email is accessed from the auth state managed in redux.
// When the page loads, we retrieve the username and immediately call the api to send the magic link.
// We handle multiple logic branches:
// - the redux state does not have a username, we show an error message
// - the api call is loading, we show a loading spinner
// - the api call is successful, we show a success message
// - the api call is unsuccessful, we show an error message

// We do NOT show different behavior for the case where the user is not registered.
// We only show a success message to prevent information leakage.

type MagicLinkState = 'loading' | 'success' | 'error' | 'invalid-username';

const SendMagicLinkPage = () => {
  const {username} = useAppSelector(state => state.auth);
  const dispatch = useAppDispatch();

  const [state, setState] = useState<MagicLinkState>('loading');
  const [error, setError] = useState<string | null>(null);

  const [sendMagicLinkMutation] = useSendMagicLinkMutation();

  const sendMagicLink = useCallback(async () => {
    try {
      if (!username) {
        setState('invalid-username');
        return;
      }

      setState('loading');
      await sendMagicLinkMutation({email: username}).unwrap();
      setError(null);
      setState('success');
      dispatch(
        setUserAccountStatus(UserValidationStatusEnum.HasActiveMagicLink)
      );
    } catch (error) {
      setError(parseErrorMessage(error));
      setState('error');
    }
  }, [username, sendMagicLinkMutation]);

  useEffect(() => {
    void sendMagicLink();
  }, [sendMagicLink]);

  return (
    <Container size={420} my={40}>
      <Stack>
        <Title ta="center" order={2}>
          Passwordless Login
        </Title>
        <Text c="dimmed" size="sm" ta="center">
          Login via email
        </Text>
        <Paper withBorder shadow="md" p={30} radius="md">
          <Stack>
            <List c="dimmed" size="sm">
              <List.Item>
                We'll send you an email with a special login link - no password
                needed!
              </List.Item>
              <List.Item>
                The link will work for{' '}
                <Text size="sm" span fw="bold">
                  60 minutes
                </Text>{' '}
                and can be used{' '}
                <Text size="sm" span fw="bold">
                  one time only
                </Text>
                .
              </List.Item>
              <List.Item>
                To use this feature, make sure you:{' '}
                <Text size="sm" span fw="bold">
                  have verified your account
                </Text>{' '}
                and can{' '}
                <Text size="sm" span fw="bold">
                  access your email
                </Text>
                . If you've logged in before, your account is already verified.
              </List.Item>
              <List.Item>
                If you're currently setting up your account or resetting your
                password, this link will take you back to finish that process
                first.
              </List.Item>
            </List>

            <Divider />

            {state === 'invalid-username' && (
              <ServerErrorAlert
                message={
                  'Your email appears to be incorrect. Please return to the login page and try again.'
                }
              />
            )}
            {state === 'loading' && (
              <Group align="center" justify="center">
                <Text c="dimmed" size="sm">
                  Sending magic link...
                </Text>
                <Loader size="xs" />
              </Group>
            )}
            {state === 'success' && (
              <Stack>
                <Alert icon={<IconMail />} color="blue" title={`Done!`}>
                  <Text size="sm">
                    Sent to{' '}
                    <Text span fs="italic">
                      {username}
                    </Text>
                    , if you have a confirmed account.
                  </Text>
                  <Text size="sm" mt="xs">
                    You may close this page now.
                  </Text>
                  <Text size="xs" c="dimmed" mt="xs">
                    If you already have a link, you will not receive another
                    email until the current one has been used or expired.
                  </Text>
                </Alert>
              </Stack>
            )}
            {state === 'error' && (
              <ServerErrorAlert
                message={
                  error ||
                  'An error occurred while sending the magic link. Please try again later. If the problem persists, contact your system administrator.'
                }
              />
            )}
          </Stack>
        </Paper>
        <Accordion variant="contained">
          <Accordion.Item value="no-email">
            <Accordion.Control>Not receiving the email?</Accordion.Control>
            <Accordion.Panel>
              <Stack>
                <Text c="dimmed" size="xs">
                  <span style={{fontWeight: 'bold'}}>
                    Here's what you can try:
                  </span>
                </Text>
                <List c="dimmed" size="xs">
                  <List.Item>
                    Make sure you have an account with this email address first.
                    Check with your administrator if you're not sure
                  </List.Item>
                  <List.Item>
                    Look in your spam/junk folder and mark our email as "not
                    spam" if you find it there
                  </List.Item>
                  <List.Item>
                    Add no-reply@proactehs.com to your email contacts
                  </List.Item>
                  <List.Item>
                    If you're at work, ask IT to allow emails from
                    no-reply@proactehs.com
                  </List.Item>
                </List>
                <Text c="dimmed" size="xs">
                  Still having trouble? Contact your administrator for help.
                </Text>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
        <Group justify="space-between">
          <Anchor
            c="dimmed"
            size="sm"
            component={Link}
            to={PathUtility.Login()}>
            ← Back to login
          </Anchor>
          <Anchor
            c="dimmed"
            size="sm"
            component={Link}
            to={PathUtility.Support()}>
            Having trouble? →
          </Anchor>
        </Group>
      </Stack>
    </Container>
  );
};

// Register the route
routeRegistry.registerPageRoute({
  backNavigation: 'transient',
  path: PathUtility.SendMagicLink(),
  element: <SendMagicLinkPage />,
  isPrivate: false,
});

export {SendMagicLinkPage};
